/**
 * Cooling System Configuration Types (Frontend)
 * TypeScript interfaces and Zod schemas for the repeating Cooling System section (#996 Phase 2)
 *
 * IMPORTANT: Zod schemas must be wrapped in useMemo() for iOS to avoid crashes
 * @see GitLab #996 — Repeating Sections (Phase 2: Cooling)
 *
 * Contract note: status/name use the GENERIC repeating-section column names
 * `status`/`name` (NOT `coolingSystemStatus`/`coolingSystemName`), mirroring the
 * Small UPS Phase 1 types.
 *
 * SHARED cooling fields (coolingIsChilledWaterAvailable, coolingAdditionalReadinessInfo)
 * are NOT part of this per-instance model — they stay on the parent request and are
 * rendered once at step level (#996 Phase 2).
 */

import { z } from 'zod';

export type CoolingSystemStatus = 'existing' | 'new';

/**
 * Cooling System Configuration interface (camelCase — matches API response)
 */
export interface CoolingSystemConfiguration {
  id?: number;
  requestId: number;

  // Existing vs New status — generic repeating-section field (#996)
  status: CoolingSystemStatus;

  // User-defined instance name (auto-named "Cooling System N" server-side when omitted)
  name?: string;

  locationTagId?: number | null;

  // Per-instance Cooling System fields (10) — keys mirror the live UI dataField keys.
  // #997 Bug 9: coolingIsChilledWaterAvailable + coolingAdditionalReadinessInfo are now
  // per-instance (previously request-level shared); each unit owns its own value.
  coolingIsExistingSystemOnsite?: string;
  coolingTotalAvailableCapacityKw?: string;
  coolingDescription?: string;
  coolingRequiredRedundancyForSystem?: string;
  coolingTempRisePrefThroughItEquipment?: string;
  coolingDescribeApproach?: string;
  coolingRearDoorHexDesired?: boolean;
  coolingDescribeRearDoorLoop?: string;
  coolingIsChilledWaterAvailable?: string;
  coolingAdditionalReadinessInfo?: string;

  // Metadata
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Input for creating a new cooling system configuration
 */
export type CreateCoolingSystemConfigurationInput = Omit<
  CoolingSystemConfiguration,
  'id' | 'createdAt' | 'updatedAt'
>;

/**
 * Input for updating a cooling system configuration
 */
export type UpdateCoolingSystemConfigurationInput = Partial<
  Omit<
    CoolingSystemConfiguration,
    'id' | 'requestId' | 'createdAt' | 'updatedAt'
  >
>;

/**
 * Grouped cooling system configurations for the segmented view (existing vs new)
 */
export interface GroupedCoolingSystemConfigurations {
  existing: CoolingSystemConfiguration[];
  new: CoolingSystemConfiguration[];
}

/**
 * API response for cooling system configurations list
 */
export interface CoolingSystemConfigurationsResponse {
  coolingSystems: CoolingSystemConfiguration[];
  total: number;
}

/**
 * Zod schema factory for cooling system configuration validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 *
 * @example
 * const schema = useMemo(() => createCoolingSystemConfigurationSchema(), []);
 */
export const createCoolingSystemConfigurationSchema = () =>
  z.object({
    id: z.number().optional(),
    requestId: z.number(),
    status: z.enum(['existing', 'new']),

    // User-defined instance name
    name: z.string().optional(),

    locationTagId: z.number().int().nullable().optional(),

    // Per-instance fields
    coolingIsExistingSystemOnsite: z.string().optional(),
    coolingTotalAvailableCapacityKw: z.string().optional(),
    coolingDescription: z.string().optional(),
    coolingRequiredRedundancyForSystem: z.string().optional(),
    coolingTempRisePrefThroughItEquipment: z.string().optional(),
    coolingDescribeApproach: z.string().optional(),
    coolingRearDoorHexDesired: z.boolean().optional(),
    coolingDescribeRearDoorLoop: z.string().optional(),
    coolingIsChilledWaterAvailable: z.string().optional(),
    coolingAdditionalReadinessInfo: z.string().optional(),

    // Metadata
    displayOrder: z.number().min(0),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  });

/**
 * Zod schema factory for create cooling system input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const createCoolingSystemInputSchema = () =>
  createCoolingSystemConfigurationSchema().omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  });

/**
 * Zod schema factory for update cooling system input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const updateCoolingSystemInputSchema = () =>
  createCoolingSystemConfigurationSchema()
    .omit({
      id: true,
      requestId: true,
      createdAt: true,
      updatedAt: true,
    })
    .partial();

/**
 * Type inference from Zod schemas
 */
export type CoolingSystemConfigurationSchema = z.infer<
  ReturnType<typeof createCoolingSystemConfigurationSchema>
>;
export type CreateCoolingSystemInputSchema = z.infer<
  ReturnType<typeof createCoolingSystemInputSchema>
>;
export type UpdateCoolingSystemInputSchema = z.infer<
  ReturnType<typeof updateCoolingSystemInputSchema>
>;

/** Status options for cooling system instances. */
export const COOLING_SYSTEM_STATUS_OPTIONS: CoolingSystemStatus[] = [
  'existing',
  'new',
];

/**
 * Default values for a new cooling system configuration
 */
export const DEFAULT_COOLING_SYSTEM_CONFIGURATION: Partial<CoolingSystemConfiguration> =
  {
    status: 'new',
    displayOrder: 0,
  };
